import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Checkbox,
  CountryFlag,
  DateInput,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  fieldGroupVariants,
  inputVariants,
  parseDateInputValue,
} from '@column/ui';

import { LabeledOption, PAYMENT_RAIL_OPTIONS, parsePaymentRails, railsToString } from '../_util/entityOptions';
import { AddressFields } from '~/components';

// State and postal code are required for U.S. addresses only.
const US_ADDRESS_RULES = {
  state: { required: 'State is required' },
  postalCode: { required: 'Postal code is required' },
};

export const EntityAddressFields: React.FC<{ isDisabled?: boolean }> = ({ isDisabled }) => {
  const countryCode = useWatch({ name: 'address.countryCode' }) as string | undefined;
  return (
    <AddressFields
      name="address"
      required
      fieldRules={countryCode === 'US' ? US_ADDRESS_RULES : undefined}
      disabled={isDisabled}
    />
  );
};

// Negative margins give a revealed AccordionItem's content room for focus rings and shadows the
// accordion would otherwise clip; only while open so closed items don't pull their neighbors in.
export const REVEAL_ITEM_CLASS = '-mx-1 border-b-0 data-[state=open]:-my-1';

export const SplitRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-3 @max-[480px]:flex-col">
    {React.Children.map(children, (child) => (
      <div className="min-w-0 flex-1">{child}</div>
    ))}
  </div>
);

interface OptionSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: LabeledOption[];
  placeholder: string;
  isDisabled?: boolean;
  hasError?: boolean;
  // Leading country flag on each option, for ISO country-code options.
  withFlags?: boolean;
}

export const OptionSelect: React.FC<OptionSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  isDisabled,
  hasError,
  withFlags,
}) => (
  <Select value={value ?? ''} onValueChange={onChange} disabled={isDisabled}>
    <SelectTrigger size="lg" className="w-full" aria-invalid={hasError || undefined}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {withFlags && <CountryFlag country={option.value} size={18} className="rounded-full" />}
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const CountrySelect: React.FC<
  Omit<OptionSelectProps, 'placeholder' | 'withFlags'> & { placeholder?: string }
> = ({ placeholder = 'Select country...', ...props }) => (
  <OptionSelect {...props} placeholder={placeholder} withFlags />
);

const FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15, ease: 'easeOut' },
} as const;

interface SensitiveInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  // Names the value in the visibility toggle's label, e.g. "Show Social Security Number".
  label: string;
  defaultVisible?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  // Takes the visibility toggle's place while the field is empty (e.g. the EIN's "Pending" checkbox).
  emptyAdornment?: React.ReactNode;
}

// A masked Input (SSN, EIN) with a trailing visibility toggle, shown once there's a value to reveal.
export const SensitiveInput: React.FC<SensitiveInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  label,
  defaultVisible = false,
  isDisabled,
  hasError,
  emptyAdornment,
}) => {
  const [isVisible, setIsVisible] = React.useState(defaultVisible);
  const hasValue = value.length > 0;

  return (
    <div className="relative flex w-full items-center">
      <Input
        type={isVisible ? 'text' : 'password'}
        autoComplete="off"
        data-1p-ignore
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={isDisabled}
        aria-invalid={hasError || undefined}
        className={cn(hasValue ? 'pr-11' : emptyAdornment && 'pr-28')}
      />
      <div className="absolute inset-y-0 right-3 flex items-center">
        <AnimatePresence initial={false} mode="wait">
          {hasValue ? (
            <motion.div key="visibility" {...FADE}>
              <Button
                type="button"
                variant="muted"
                size="icon-xs"
                aria-label={isVisible ? `Hide ${label}` : `Show ${label}`}
                onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                onClick={() => setIsVisible((visible) => !visible)}
              >
                {isVisible ? (
                  <Icon.EyeClosed className="[--icon-size:16px]" />
                ) : (
                  <Icon.Eye className="[--icon-size:16px]" />
                )}
              </Button>
            </motion.div>
          ) : (
            emptyAdornment && (
              <motion.div key="empty" className="flex items-center gap-2" {...FADE}>
                {emptyAdornment}
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isValidIsoDate = (value: string): boolean => {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export const parseIsoDate = (value?: string): Date | undefined => {
  if (!value || !isValidIsoDate(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const validateDate = (label: string) => (value: unknown) =>
  !value || (typeof value === 'string' && isValidIsoDate(value)) || `${label} must be a valid date (MM/DD/YYYY).`;

// The form stores dates as YYYY-MM-DD; the DateInput edits them as MM/DD/YYYY text.
const isoToText = (value: string): string => {
  const match = ISO_DATE.exec(value);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
};

const textToIso = (text: string): string | undefined => {
  if (!parseDateInputValue(text)) return undefined;
  const [month, day, year] = text.split('/');
  return `${year}-${month}-${day}`;
};

interface DateTextInputProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  hasError?: boolean;
}

// A typed MM / DD / YYYY date (the @column/ui DateInput) for dates decades in the past, e.g. date of
// birth. A complete date commits as YYYY-MM-DD; a partial entry stays local while typing and commits
// as typed on blur, so `validateDate` can flag it instead of the value silently clearing.
export const DateTextInput: React.FC<DateTextInputProps> = ({ value, onChange, onBlur, isDisabled, hasError }) => {
  const [text, setText] = React.useState(() => isoToText(value ?? ''));

  // Re-sync when the form value changes from outside this input (a reset or a loaded entity).
  React.useEffect(() => {
    setText((current) => ((textToIso(current) ?? current) === (value ?? '') ? current : isoToText(value ?? '')));
  }, [value]);

  const commit = (next: string) => onChange(textToIso(next) ?? next);

  return (
    <DateInput
      value={text}
      onValueChange={(next) => {
        setText(next);
        if (!next || next.length === 'MM/DD/YYYY'.length) {
          commit(next);
        }
      }}
      onBlur={() => {
        if (text !== isoToText(value ?? '')) {
          commit(text);
        }
        onBlur?.();
      }}
      disabled={isDisabled}
      aria-invalid={hasError}
    />
  );
};

interface YesNoInputProps {
  value?: unknown;
  onChange: (value: boolean) => void;
  isDisabled?: boolean;
}

export const YesNoInput: React.FC<YesNoInputProps> = ({ value, onChange, isDisabled }) => {
  const groupId = React.useId();
  const selected = value === true ? 'yes' : value === false ? 'no' : '';
  return (
    <RadioGroup
      className={fieldGroupVariants({ variant: 'outline', columns: 2 })}
      value={selected}
      onValueChange={(next) => onChange(next === 'yes')}
      disabled={isDisabled}
    >
      <Field orientation="horizontal">
        <RadioGroupItem value="yes" id={`${groupId}-yes`} disabled={isDisabled} />
        <FieldLabel htmlFor={`${groupId}-yes`}>Yes</FieldLabel>
      </Field>
      <Field orientation="horizontal">
        <RadioGroupItem value="no" id={`${groupId}-no`} disabled={isDisabled} />
        <FieldLabel htmlFor={`${groupId}-no`}>No</FieldLabel>
      </Field>
    </RadioGroup>
  );
};

interface CheckboxInputProps {
  checked?: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  isDisabled?: boolean;
}

export const CheckboxInput: React.FC<CheckboxInputProps> = ({ checked, onChange, label, isDisabled }) => {
  const id = React.useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked === true}
        disabled={isDisabled}
        onCheckedChange={(next) => onChange(next === true)}
      />
      {label && <Label htmlFor={id}>{label}</Label>}
    </div>
  );
};

interface PaymentRailsInputProps {
  // Comma-separated string of payment-rail values, e.g. "ach,wire".
  value?: unknown;
  onChange: (value: string) => void;
  isDisabled?: boolean;
}

export const PaymentRailsInput: React.FC<PaymentRailsInputProps> = ({ value, onChange, isDisabled }) => {
  const uid = React.useId();
  const selected = parsePaymentRails(value as string | undefined);

  const toggleRail = (rail: string, checked: boolean) => {
    const next = checked ? [...selected, rail] : selected.filter((selectedRail) => selectedRail !== rail);
    onChange(railsToString(next));
  };

  return (
    <FieldGroup variant="outline" columns={2}>
      {PAYMENT_RAIL_OPTIONS.map((option) => {
        const checkboxId = `${uid}-${option.value}`;

        return (
          <Field key={option.value} orientation="horizontal">
            <Checkbox
              id={checkboxId}
              checked={selected.includes(option.value)}
              disabled={isDisabled}
              onCheckedChange={(checked) => toggleRail(option.value, checked === true)}
            />
            <FieldLabel htmlFor={checkboxId}>{option.label}</FieldLabel>
          </Field>
        );
      })}
    </FieldGroup>
  );
};

interface StringListInputProps {
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

// Edits an API string[] field as a comma-separated text field while keeping the form value a real
// string[]. A local draft preserves exactly what's typed (including trailing separators) so the
// caret never jumps; the parsed array is pushed to the form on every keystroke, and the draft is
// re-synced from the form value whenever the field isn't being edited.
export const StringListInput: React.FC<StringListInputProps> = ({ value, onChange, placeholder, isDisabled }) => {
  const [draft, setDraft] = React.useState(() => (value ?? []).join(', '));
  const editingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editingRef.current) {
      setDraft((value ?? []).join(', '));
    }
  }, [value]);

  const parse = (text: string): string[] =>
    text
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

  return (
    <Input
      className="w-full"
      value={draft}
      placeholder={placeholder}
      disabled={isDisabled}
      onFocus={() => {
        editingRef.current = true;
      }}
      onBlur={() => {
        editingRef.current = false;
        setDraft((value ?? []).join(', '));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parse(event.target.value));
      }}
    />
  );
};

interface NumberListInputProps {
  value?: number[];
  onChange: (value: number[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

// The number[] counterpart to StringListInput (e.g. person income ranges): same comma-separated
// draft behavior, but each segment is parsed to a number and non-numeric segments are dropped.
export const NumberListInput: React.FC<NumberListInputProps> = ({ value, onChange, placeholder, isDisabled }) => {
  const [draft, setDraft] = React.useState(() => (value ?? []).join(', '));
  const editingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editingRef.current) {
      setDraft((value ?? []).join(', '));
    }
  }, [value]);

  const parse = (text: string): number[] =>
    text
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));

  return (
    <Input
      className="w-full"
      value={draft}
      placeholder={placeholder}
      disabled={isDisabled}
      inputMode="numeric"
      onFocus={() => {
        editingRef.current = true;
      }}
      onBlur={() => {
        editingRef.current = false;
        setDraft((value ?? []).join(', '));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parse(event.target.value));
      }}
    />
  );
};

interface PercentageInputProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  isDisabled?: boolean;
  hasError?: boolean;
}

const clampPercentage = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

// A whole-number percentage input: the "%" suffix trails the digits as they're typed, entry is
// capped to 0–100, and ArrowUp/ArrowDown step the value while focused. Built on NumericFormat and
// styled with the shared inputVariants so it matches the other @column/ui inputs (NumericFormat owns
// its own <input>, mirroring AmountInput, since @column/ui's Input isn't a forwardRef component).
export const PercentageInput: React.FC<PercentageInputProps> = ({ value, onChange, isDisabled, hasError }) => (
  <NumericFormat
    className={inputVariants()}
    value={value ?? ''}
    onValueChange={(values) => onChange(values.floatValue)}
    suffix="%"
    decimalScale={0}
    allowNegative={false}
    isAllowed={({ floatValue }) => floatValue === undefined || (floatValue >= 0 && floatValue <= 100)}
    placeholder="0%"
    inputMode="numeric"
    disabled={isDisabled}
    aria-invalid={hasError || undefined}
    onKeyDown={(event) => {
      if (isDisabled || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
        return;
      }
      event.preventDefault();
      const current = typeof value === 'number' && Number.isFinite(value) ? value : 0;
      onChange(clampPercentage(current + (event.key === 'ArrowUp' ? 1 : -1)));
    }}
  />
);
